/**
 * Phase 105-Agent-B — ProofCapture.
 *
 * One reusable Dialog for BOTH pickup and delivery proof. Captures:
 *   • Photo (camera-first via input capture="environment", file picker
 *     fallback when camera permission denied)
 *   • 6-digit OTP (sender for pickup, recipient for delivery)
 *   • Browser Geolocation (auto-attaches lat/lng/accuracy on submit;
 *     graceful no-op if denied)
 *   • Recipient name (delivery only)
 *
 * Endpoints:
 *   • kind="pickup"   → POST /api/orders/{tid}/pickup-complete
 *   • kind="delivery" → POST /api/orders/{tid}/delivery-complete
 *
 * Sprint-105e 3-row flex dialog layout. Sprint-105d submitBlockedReason.
 */
import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Camera, X, Loader2, MapPin, ShieldAlert, ImagePlus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../../services/api";

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function compress(dataUrl, maxSide = 1280, quality = 0.82) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(1, maxSide / Math.max(img.width, img.height));
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

export default function ProofCapture({ open, onOpenChange, order, kind, onSuccess }) {
  const isPickup = kind === "pickup";
  const verb = isPickup ? "Pickup" : "Delivery";
  const otpHint = isPickup ? "Ask the sender for the 6-digit pickup OTP" : "Ask the recipient for the 6-digit delivery OTP";

  const [photoB64, setPhotoB64] = useState(null);
  const [otp, setOtp] = useState("");
  const [recipient, setRecipient] = useState("");
  const [note, setNote] = useState("");
  const [geo, setGeo] = useState(null);
  const [geoStatus, setGeoStatus] = useState("idle"); // idle, locating, ok, denied, unavailable
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // Capture geo on open.
  useEffect(() => {
    if (!open) return;
    setPhotoB64(null);
    setOtp("");
    setRecipient("");
    setNote("");
    setGeo(null);
    if (!navigator.geolocation) {
      setGeoStatus("unavailable");
      return;
    }
    setGeoStatus("locating");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeo({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
        });
        setGeoStatus("ok");
      },
      () => { setGeoStatus("denied"); },
      { enableHighAccuracy: true, timeout: 12_000 },
    );
  }, [open]);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const raw = await readFileAsBase64(f);
      const compressed = await compress(raw);
      setPhotoB64(compressed);
    } catch {
      toast.error("Couldn't read that photo");
    }
  };

  const retryGeo = () => {
    if (!navigator.geolocation) return;
    setGeoStatus("locating");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setGeo({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy_m: pos.coords.accuracy }); setGeoStatus("ok"); },
      () => { setGeoStatus("denied"); },
      { enableHighAccuracy: true, timeout: 12_000 },
    );
  };

  const otpValid = /^\d{4,8}$/.test(otp.trim());
  const submitBlockedReason = !otpValid
    ? "Enter the OTP (4-8 digits)"
    : !photoB64
      ? "Capture or upload a proof photo first"
      : null;

  const submit = async () => {
    if (submitBlockedReason) {
      toast.error(submitBlockedReason);
      return;
    }
    setSubmitting(true);
    try {
      const path = isPickup ? "pickup-complete" : "delivery-complete";
      const body = {
        image_base64: photoB64,
        otp: otp.trim(),
        ...(geo ? { lat: geo.lat, lng: geo.lng, accuracy_m: geo.accuracy_m } : {}),
        ...(note?.trim() ? { note: note.trim() } : {}),
        ...(isPickup ? {} : recipient?.trim() ? { recipient_name: recipient.trim() } : {}),
      };
      const { data } = await api.post(`/orders/${order.tracking_id}/${path}`, body);
      toast.success(`${verb} proof recorded — status: ${data?.status || "updated"}.`);
      if (onSuccess) onSuccess(data);
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || `Couldn't submit ${verb.toLowerCase()} proof`;
      toast.error(typeof detail === "string" ? detail : `Couldn't submit ${verb.toLowerCase()} proof`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
      <DialogContent
        className="p-0 flex flex-col gap-0 max-h-[90vh] sm:max-w-md rounded-2xl overflow-hidden"
        data-testid={`proof-capture-${kind}`}
      >
        <DialogHeader className="flex-shrink-0 px-5 pt-5 pb-3 border-b border-slate-100">
          <DialogTitle className="font-['Outfit'] text-lg font-bold text-slate-900 flex items-center gap-2">
            <Camera className="h-5 w-5 text-emerald-600" /> Capture {verb.toLowerCase()} proof
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Photo + OTP + geo-tag for <span className="font-mono">{order?.tracking_id}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="proof-capture-body">
          {/* Photo */}
          <section data-testid="proof-photo-block">
            <p className="text-[11px] uppercase font-bold tracking-wider text-slate-400 mb-1.5">Photo</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFile}
              className="hidden"
              data-testid="proof-photo-input"
            />
            {photoB64 ? (
              <div className="relative w-full rounded-xl overflow-hidden border border-slate-200">
                <img src={photoB64} alt="proof" className="w-full max-h-72 object-contain bg-slate-100" />
                <button
                  type="button"
                  onClick={() => { setPhotoB64(null); fileInputRef.current?.click?.(); }}
                  className="absolute top-2 right-2 bg-white/90 hover:bg-white text-xs font-semibold px-2.5 py-1.5 rounded-full shadow flex items-center gap-1"
                  data-testid="proof-photo-retake"
                >
                  <RefreshCw className="h-3.5 w-3.5" /> Retake
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-slate-300 hover:border-emerald-400 hover:bg-emerald-50/40 rounded-xl py-8 flex flex-col items-center gap-2 text-slate-500 font-semibold text-sm transition"
                data-testid="proof-photo-capture-btn"
              >
                <ImagePlus className="h-7 w-7" />
                Take or upload a photo
              </button>
            )}
          </section>

          {/* OTP */}
          <section data-testid="proof-otp-block">
            <p className="text-[11px] uppercase font-bold tracking-wider text-slate-400 mb-1.5">OTP</p>
            <input
              type="text"
              inputMode="numeric"
              maxLength={8}
              placeholder="6-digit code"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))}
              className="w-full text-center font-mono text-lg tracking-[0.5em] rounded-xl border-2 border-slate-200 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-200 focus:outline-none py-2.5"
              data-testid="proof-otp-input"
            />
            <p className="text-[11px] text-slate-500 mt-1">{otpHint}</p>
          </section>

          {/* Recipient name (delivery only) */}
          {!isPickup && (
            <section>
              <p className="text-[11px] uppercase font-bold tracking-wider text-slate-400 mb-1.5">Recipient name (optional)</p>
              <input
                type="text"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value.slice(0, 80))}
                placeholder="Name of person who received"
                className="w-full text-sm rounded-xl border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                data-testid="proof-recipient-input"
              />
            </section>
          )}

          {/* Note */}
          <section>
            <p className="text-[11px] uppercase font-bold tracking-wider text-slate-400 mb-1.5">Note (optional)</p>
            <textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 280))}
              placeholder="Anything dispatch should know?"
              className="w-full text-sm rounded-xl border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-200"
              data-testid="proof-note-input"
            />
          </section>

          {/* Geo status */}
          <section
            className={`rounded-xl border p-3 flex items-center gap-2 ${
              geoStatus === "ok" ? "border-emerald-200 bg-emerald-50/60 text-emerald-800"
              : geoStatus === "locating" ? "border-slate-200 bg-slate-50 text-slate-600"
              : "border-amber-200 bg-amber-50/60 text-amber-800"
            }`}
            data-testid="proof-geo-block"
          >
            <MapPin className="h-4 w-4 shrink-0" />
            <div className="flex-1 text-xs">
              {geoStatus === "locating" && "Locating you…"}
              {geoStatus === "ok" && geo && (
                <span>
                  Location attached · <span className="font-mono">{geo.lat.toFixed(5)}, {geo.lng.toFixed(5)}</span>
                  {geo.accuracy_m != null && <> · ±{Math.round(geo.accuracy_m)} m</>}
                </span>
              )}
              {(geoStatus === "denied" || geoStatus === "unavailable") && (
                <span>Geolocation unavailable — proof will submit without coordinates.</span>
              )}
            </div>
            {geoStatus !== "ok" && geoStatus !== "locating" && (
              <button type="button" onClick={retryGeo} className="text-[11px] font-bold underline">
                Retry
              </button>
            )}
          </section>
        </div>

        <div className="flex-shrink-0 px-5 py-3 border-t border-slate-100 bg-white space-y-2" data-testid="proof-capture-footer">
          {submitBlockedReason && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 flex items-center gap-1.5" data-testid="proof-blocked-reason">
              <ShieldAlert className="h-3.5 w-3.5" /> Almost there — {submitBlockedReason}
            </p>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold flex items-center gap-1"
              data-testid="proof-cancel-btn"
            >
              <X className="h-4 w-4" /> Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!!submitBlockedReason || submitting}
              title={submitBlockedReason || undefined}
              aria-disabled={!!submitBlockedReason || submitting}
              className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition ${
                submitBlockedReason || submitting
                  ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                  : "bg-emerald-600 text-white hover:bg-emerald-700"
              }`}
              data-testid="proof-submit-btn"
            >
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : `Submit ${verb.toLowerCase()} proof`}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
